/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { 
  Col, 
  Row, 
  Card, 
  Badge, 
  Table, 
  Button, 
  Form, 
  Tab, 
  Tabs,
  Dropdown,
  InputGroup,
  FormControl,
  Modal,
  Nav
} from "react-bootstrap";
import { MapContainer, TileLayer, FeatureGroup, useMap, Popup, Marker } from 'react-leaflet';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import * as turf from '@turf/turf';
import axios from 'axios';
import DatePicker from 'components/base/DatePicker';
import { 
  Activity, 
  AlertTriangle, 
  ChevronRight, 
  Lock, 
  Clock, 
  DownloadCloud, 
  Eye, 
  Globe, 
  HardDrive, 
  LucideProps, 
  Map as MapIcon, 
  MapPin, 
  RefreshCw, 
  Search, 
  Server, 
  Settings, 
  Shield, 
  Terminal, 
  User, 
  Wifi,
  Layers,
  Navigation,
  Building,
  School,
  Ambulance,
  Ruler,
  Pencil,
  BarChart3,
  PieChart,
  LineChart,
  AreaChart,
  Calendar,
  Filter,
  Send,
  Image,
  Paperclip,
  MessageCircle,
  Plus,
  Save,
  Award,
  File,
  FileText,
  Type,
  List,
  CheckSquare,
  UploadCloud
} from 'lucide-react';
import 'leaflet-draw';

// Crear un contexto para compartir estado entre componentes
const XGISDashboardContext = React.createContext<any>({});

// Nueva interfaz para mensajes de chat
interface ChatMessage {
  id: string;
  poiId: string;
  content: string;
  timestamp: Date;
  sender: 'user' | 'system' | 'ai';
  contentType: 'text' | 'image' | 'file' | 'form' | 'heading' | 'list' | 'checkbox';
  metadata?: {
    fileName?: string;
    fileUrl?: string;
    formData?: Record<string, any>;
    checkboxItems?: Array<{id: string, text: string, checked: boolean}>;
    listItems?: Array<string>;
  };
}

// Ampliar la interfaz POI para incluir chat
interface POI {
  id: string;
  name: string;
  lat: number;
  lng: number;
  type: 'landmark' | 'emergency' | 'school' | 'commercial' | 'custom';
  status: 'active' | 'inactive' | 'warning';
  lastUpdate?: Date;
  metadata: Record<string, any>;
  chatMessages?: ChatMessage[];
}

// Interfaces existentes
interface GISTool {
  id: string;
  name: string;
  category: 'analysis' | 'drawing' | 'measurement' | 'import' | 'export' | 'other';
  status: 'running' | 'stopped' | 'error';
  lastRun?: Date;
  description: string;
  command: string;
}

// GIS Stat Component
interface GISStatProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  variant: string;
  trend?: number;
}

interface ChartProps {
  type: 'line' | 'bar' | 'pie' | 'area';
  data: any;
  className?: string;
  height?: number;
}

interface GeoAlertsListProps {
  alerts: GeoAlert[];
}

interface GISToolsProps {
  tools: GISTool[];
  onSelect: (toolId: string) => void;
  activeToolId: string | undefined;
}

interface IconWrapperProps {
  icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>>;
  className?: string;
  size?: number;
  color?: string;
}

interface POIsListProps {
  points: POI[];
  onSelect: (poiId: string) => void;
  activePoiId: string | undefined;
}

interface GeoAlert {
  id: string;
  type: 'boundary' | 'proximity' | 'environmental' | 'traffic' | 'security' | 'custom';
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  location: {
    lat: number;
    lng: number;
  };
  radius?: number;
  timestamp: Date;
  message: string;
  resolved: boolean;
}

interface AnalysisResult {
  id: string;
  toolId: string;
  targetId?: string;
  timestamp: Date;
  findings: number;
  details: string;
  status: 'completed' | 'failed' | 'in_progress';
}

interface Zone {
  id: string;
  type: 'area' | 'route' | 'emergency' | 'dispersion' | 'custom';
  name: string;
  coordinates: Array<Array<[number, number]>>;
  metadata: Record<string, any>;
}

interface DispersionPlume {
  id: string;
  source: { lat: number; lng: number };
  direction: number;
  speed: number;
  concentration: number;
  coordinates: Array<Array<[number, number]>>;
}

interface HistoricalData {
  date: string;
  pois: number;
  alerts: number;
  plumes: number;
  zones: number;
}

interface MapInitializerProps {
  onMapReady: (map: L.Map) => void;
}

// Nuevo componente para el chat de un punto
interface POIChatProps {
  poi: POI;
  onMessageSend: (poiId: string, messageContent: string, contentType: ChatMessage['contentType'], metadata?: any) => void;
  onClose: () => void;
}

// Componente para mensajes individuales de chat
interface ChatMessageItemProps {
  message: ChatMessage;
}

// Servicio mock para almacenar mensajes de chat
const chatService = {
  messages: [] as ChatMessage[],
  
  // Añadir un mensaje
  addMessage(message: ChatMessage): Promise<ChatMessage> {
    this.messages.push(message);
    return Promise.resolve(message);
  },
  
  // Obtener mensajes para un POI específico
  getMessagesForPOI(poiId: string): Promise<ChatMessage[]> {
    return Promise.resolve(this.messages.filter(msg => msg.poiId === poiId));
  },
  
  // Simular respuesta de IA
  getAIResponse(message: string): Promise<string> {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (message.toLowerCase().includes('hola') || message.toLowerCase().includes('saludos')) {
          resolve('¡Hola! ¿En qué puedo ayudarte con este punto en el mapa?');
        } else if (message.toLowerCase().includes('crear') || message.toLowerCase().includes('formulario')) {
          resolve('Puedo ayudarte a crear un formulario. ¿Qué campos necesitas incluir?');
        } else if (message.toLowerCase().includes('imagen') || message.toLowerCase().includes('foto')) {
          resolve('Para añadir una imagen, usa el botón de adjuntar o arrastra la imagen al chat.');
        } else if (message.toLowerCase().includes('titulo') || message.toLowerCase().includes('título')) {
          resolve('Para crear un título, usa el comando /titulo seguido del texto.');
        } else if (message.toLowerCase().includes('lista')) {
          resolve('Para crear una lista, usa el comando /lista seguido de los elementos separados por comas.');
        } else if (message.toLowerCase().includes('guardar')) {
          resolve('Los datos de este punto han sido guardados correctamente.');
        } else {
          resolve('Entiendo. ¿Hay algo específico que quieras hacer con este punto en el mapa?');
        }
      }, 1000);
    });
  }
};

// Componente de mensaje individual
const ChatMessageItem: React.FC<ChatMessageItemProps> = ({ message }) => {
  const isUser = message.sender === 'user';
  const isAI = message.sender === 'ai';
  
  const renderContent = () => {
    switch (message.contentType) {
      case 'text':
        return <p className="mb-0">{message.content}</p>;
        
      case 'image':
        return (
          <div>
            <img 
              src={message.metadata?.fileUrl || '/placeholder-image.jpg'} 
              alt="Imagen compartida" 
              className="img-fluid rounded" 
              style={{ maxHeight: '200px' }}
            />
            <p className="mb-0 mt-1 small text-muted">{message.content}</p>
          </div>
        );
        
      case 'file':
        return (
          <div className="d-flex align-items-center">
            <IconWrapper icon={File} className="me-2" size={24} />
            <div>
              <p className="mb-0">{message.metadata?.fileName || 'Archivo adjunto'}</p>
              <p className="mb-0 small text-muted">{message.content}</p>
            </div>
          </div>
        );
        
      case 'form':
        return (
          <div className="border rounded p-2">
            <h6 className="mb-2">{message.content}</h6>
            {message.metadata?.formData && Object.entries(message.metadata.formData).map(([key, value]) => (
              <div key={key} className="mb-1">
                <strong>{key}:</strong> {value}
              </div>
            ))}
          </div>
        );
        
      case 'heading':
        return <h5 className="mb-1">{message.content}</h5>;
        
      case 'list':
        return (
          <div>
            <p className="mb-1">{message.content}</p>
            <ul className="mb-0">
              {message.metadata?.listItems?.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        );
        
      case 'checkbox':
        return (
          <div>
            <p className="mb-1">{message.content}</p>
            <div>
              {message.metadata?.checkboxItems?.map((item) => (
                <div key={item.id} className="form-check">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    checked={item.checked} 
                    id={item.id} 
                    readOnly 
                  />
                  <label className="form-check-label" htmlFor={item.id}>
                    {item.text}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );
        
      default:
        return <p className="mb-0">{message.content}</p>;
    }
  };
  
  return (
    <div className={`d-flex ${isUser ? 'justify-content-end' : ''} mb-3`}>
      <div 
        className={`chat-message p-2 rounded ${
          isUser ? 'bg-primary text-white' : 
          isAI ? 'bg-info bg-opacity-10' : 'bg-light'
        }`}
        style={{ maxWidth: '80%' }}
      >
        {renderContent()}
        <div className="d-flex justify-content-between align-items-center mt-1">
          <small className={`${isUser ? 'text-white-50' : 'text-muted'}`}>
            {message.sender === 'user' ? 'Tú' : message.sender === 'ai' ? 'Asistente IA' : 'Sistema'}
          </small>
          <small className={`ms-2 ${isUser ? 'text-white-50' : 'text-muted'}`}>
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </small>
        </div>
      </div>
    </div>
  );
};

// Componente de Chat para POI
const POIChat: React.FC<POIChatProps> = ({ poi, onMessageSend, onClose }) => {
  const [inputValue, setInputValue] = useState('');
  const [activeTab, setActiveTab] = useState<'chat' | 'forms' | 'content'>('chat');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Estado para el formulario personalizado
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formFields, setFormFields] = useState<Array<{id: string, label: string, type: string}>>([]);
  
  // Estado para la lista personalizada
  const [showListBuilder, setShowListBuilder] = useState(false);
  const [listTitle, setListTitle] = useState('');
  const [listItems, setListItems] = useState<string[]>(['']);
  
  // Para subida de archivos
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Cargar mensajes al inicio
  useEffect(() => {
    chatService.getMessagesForPOI(poi.id)
      .then(msgs => {
        setMessages(msgs);
      });
  }, [poi.id]);
  
  // Scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Manejar envío de mensaje
  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    
    // Detectar comandos especiales
    if (inputValue.startsWith('/titulo ')) {
      const headingText = inputValue.slice('/titulo '.length);
      handleSendContentMessage(headingText, 'heading');
    } 
    else if (inputValue.startsWith('/lista ')) {
      const listText = inputValue.slice('/lista '.length);
      const items = listText.split(',').map(item => item.trim());
      handleSendListMessage(items);
    }
    else {
      // Mensaje normal de texto
      const newMessage: ChatMessage = {
        id: `msg_${Date.now()}`,
        poiId: poi.id,
        content: inputValue,
        timestamp: new Date(),
        sender: 'user',
        contentType: 'text'
      };
      
      setInputValue('');
      setMessages(prev => [...prev, newMessage]);
      
      // Guardar el mensaje
      await chatService.addMessage(newMessage);
      
      // Mostrar estado de carga
      setIsLoading(true);
      
      // Simular respuesta de la IA
      try {
        const aiResponse = await chatService.getAIResponse(inputValue);
        
        const responseMessage: ChatMessage = {
          id: `msg_${Date.now()}_ai`,
          poiId: poi.id,
          content: aiResponse,
          timestamp: new Date(),
          sender: 'ai',
          contentType: 'text'
        };
        
        setMessages(prev => [...prev, responseMessage]);
        await chatService.addMessage(responseMessage);
      } catch (error) {
        console.error('Error al obtener respuesta de IA:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  // Manejar envío de mensaje con contenido especial
  const handleSendContentMessage = (content: string, contentType: ChatMessage['contentType'], metadata?: any) => {
    const newMessage: ChatMessage = {
      id: `msg_${Date.now()}`,
      poiId: poi.id,
      content,
      timestamp: new Date(),
      sender: 'user',
      contentType,
      metadata
    };
    
    setMessages(prev => [...prev, newMessage]);
    chatService.addMessage(newMessage);
    
    // Notificar al componente padre
    onMessageSend(poi.id, content, contentType, metadata);
  };
  
  // Manejar envío de lista
  const handleSendListMessage = (items: string[]) => {
    handleSendContentMessage(
      'Lista de elementos:',
      'list',
      { listItems: items }
    );
  };
  
  // Crear un formulario
  const handleCreateForm = () => {
    if (!formTitle.trim() || formFields.length === 0) return;
    
    // Crear un objeto con los campos del formulario
    const formData = formFields.reduce((acc, field) => {
      acc[field.label] = `[Campo de tipo: ${field.type}]`;
      return acc;
    }, {} as Record<string, string>);
    
    handleSendContentMessage(
      formTitle,
      'form',
      { formData }
    );
    
    // Cerrar el constructor de formularios
    setShowFormBuilder(false);
    setFormTitle('');
    setFormFields([]);
  };
  
  // Añadir campo al formulario
  const addFormField = () => {
    setFormFields(prev => [
      ...prev, 
      { 
        id: `field_${Date.now()}`, 
        label: `Campo ${prev.length + 1}`, 
        type: 'text' 
      }
    ]);
  };
  
  // Actualizar campo del formulario
  const updateFormField = (id: string, key: string, value: string) => {
    setFormFields(prev => 
      prev.map(field => 
        field.id === id ? { ...field, [key]: value } : field
      )
    );
  };
  
  // Eliminar campo del formulario
  const removeFormField = (id: string) => {
    setFormFields(prev => prev.filter(field => field.id !== id));
  };
  
  // Manejar carga de archivo
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const isImage = file.type.startsWith('image/');
    const contentType = isImage ? 'image' : 'file';
    
    // En un escenario real, aquí subirías el archivo a tu servidor
    // y obtendrías una URL. Para este ejemplo, creamos una URL local.
    const fileUrl = URL.createObjectURL(file);
    
    handleSendContentMessage(
      `${isImage ? 'Imagen' : 'Archivo'}: ${file.name}`,
      contentType,
      {
        fileName: file.name,
        fileUrl: fileUrl
      }
    );
    
    // Limpiar el input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Añadir elemento a la lista
  const addListItem = () => {
    setListItems(prev => [...prev, '']);
  };
  
  // Actualizar elemento de la lista
  const updateListItem = (index: number, value: string) => {
    setListItems(prev => {
      const newItems = [...prev];
      newItems[index] = value;
      return newItems;
    });
  };
  
  // Eliminar elemento de la lista
  const removeListItem = (index: number) => {
    setListItems(prev => prev.filter((_, i) => i !== index));
  };
  
  // Crear lista
  const handleCreateList = () => {
    if (!listTitle.trim() || listItems.filter(item => item.trim()).length === 0) return;
    
    handleSendContentMessage(
      listTitle,
      'list',
      { listItems: listItems.filter(item => item.trim()) }
    );
    
    setShowListBuilder(false);
    setListTitle('');
    setListItems(['']);
  };
  
  return (
    <div className="h-100 d-flex flex-column">
      <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
        <h5 className="mb-0">{poi.name}</h5>
        <Button variant="outline-secondary" size="sm" onClick={onClose}>
          ×
        </Button>
      </div>
      
      <Nav variant="tabs" className="px-2 pt-2">
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'chat'} 
            onClick={() => setActiveTab('chat')}
            className="d-flex align-items-center"
          >
            <MessageCircle size={16} className="me-1" />
            Chat
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'forms'} 
            onClick={() => setActiveTab('forms')}
            className="d-flex align-items-center"
          >
            <CheckSquare size={16} className="me-1" />
            Formularios
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'content'} 
            onClick={() => setActiveTab('content')}
            className="d-flex align-items-center"
          >
            <Type size={16} className="me-1" />
            Contenido
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      <div className="flex-grow-1 p-3 overflow-auto">
        {activeTab === 'chat' && (
          <>
            {messages.map(message => (
              <ChatMessageItem key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="d-flex justify-content-start mb-3">
                <div className="bg-light p-2 rounded">
                  <div className="spinner-grow spinner-grow-sm text-primary me-1" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <div className="spinner-grow spinner-grow-sm text-primary me-1" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <div className="spinner-grow spinner-grow-sm text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef}></div>
          </>
        )}
        
        {activeTab === 'forms' && (
          <div>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Formularios Personalizados</h6>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => setShowFormBuilder(true)}
              >
                <Plus size={16} className="me-1" />
                Nuevo Formulario
              </Button>
            </div>
            
            {messages.filter(m => m.contentType === 'form').length > 0 ? (
              <div>
                {messages
                  .filter(m => m.contentType === 'form')
                  .map(message => (
                    <Card key={message.id} className="mb-2">
                      <Card.Body>
                        <Card.Title>{message.content}</Card.Title>
                        {message.metadata?.formData && Object.entries(message.metadata.formData).map(([key, value]) => (
                          <div key={key} className="mb-1">
                            <strong>{key}:</strong> {value}
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  ))
                }
              </div>
            ) : (
              <div className="text-center p-4 text-muted">
                <IconWrapper icon={CheckSquare} size={32} className="mb-2" />
                <p>No hay formularios creados aún</p>
              </div>
            )}
            
            {/* Modal Constructor de Formularios */}
            <Modal show={showFormBuilder} onHide={() => setShowFormBuilder(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Crear Formulario</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Título del Formulario</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Ingrese un título"
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                    />
                  </Form.Group>
                  
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Campos</h6>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={addFormField}
                      >
                        <Plus size={16} />
                      </Button>
                    </div>
                    
                    {formFields.map((field) => (
                      <div key={field.id} className="border p-2 rounded mb-2">
                        <div className="d-flex justify-content-between mb-2">
                          <h6 className="mb-0">Campo</h6>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => removeFormField(field.id)}
                          >
                            ×
                          </Button>
                        </div>
                        <Form.Group className="mb-2">
                          <Form.Label>Etiqueta</Form.Label>
                          <Form.Control 
                            type="text" 
                            value={field.label}
                            onChange={(e) => updateFormField(field.id, 'label', e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Tipo</Form.Label>
                          <Form.Select
                            value={field.type}
                            onChange={(e) => updateFormField(field.id, 'type', e.target.value)}
                          >
                            <option value="text">Texto</option>
                            <option value="number">Número</option>
                            <option value="date">Fecha</option>
                            <option value="email">Email</option>
                            <option value="textarea">Área de Texto</option>
                            <option value="select">Lista de Selección</option>
                            <option value="checkbox">Casilla de Verificación</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                    ))}
                    
                    {formFields.length === 0 && (
                      <div className="text-center p-3 border rounded text-muted">
                        Añade campos al formulario
                      </div>
                    )}
                  </div>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowFormBuilder(false)}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleCreateForm}>
                  Crear Formulario
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        )}
        
        {activeTab === 'content' && (
          <div>
            <div className="mb-3">
              <h6>Elementos de Contenido</h6>
              <p className="text-muted small">Añade diferentes tipos de contenido a este punto.</p>
            </div>
            
            <div className="row g-2 mb-3">
              <div className="col-4">
                <Button 
                  variant="outline-secondary" 
                  className="w-100 py-3 d-flex flex-column align-items-center"
                  onClick={() => {
                    const headingText = prompt('Ingresa el texto para el título:');
                    if (headingText) {
                      handleSendContentMessage(headingText, 'heading');
                    }
                  }}
                >
                  <Type size={24} className="mb-2" />
                  <span>Título</span>
                </Button>
              </div>
              
              <div className="col-4">
                <Button 
                  variant="outline-secondary" 
                  className="w-100 py-3 d-flex flex-column align-items-center"
                  onClick={() => setShowListBuilder(true)}
                >
                  <List size={24} className="mb-2" />
                  <span>Lista</span>
                </Button>
              </div>
              
              <div className="col-4">
                <Button 
                  variant="outline-secondary" 
                  className="w-100 py-3 d-flex flex-column align-items-center"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Image size={24} className="mb-2" />
                  <span>Imagen</span>
                </Button>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  className="d-none"
                  accept="image/*"
                  onChange={handleFileUpload}
                />
              </div>
              
              <div className="col-4">
                <Button 
                  variant="outline-secondary" 
                  className="w-100 py-3 d-flex flex-column align-items-center"
                  onClick={() => {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.onchange = (e: any) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      // En un escenario real, aquí subirías el archivo a tu servidor
                      // y obtendrías una URL. Para este ejemplo, creamos una URL local.
                      const fileUrl = URL.createObjectURL(file);
                      
                      handleSendContentMessage(
                        `Archivo: ${file.name}`,
                        'file',
                        {
                          fileName: file.name,
                          fileUrl: fileUrl
                        }
                      );
                    };
                    fileInput.click();
                  }}
                >
                  <FileText size={24} className="mb-2" />
                  <span>Archivo</span>
                </Button>
              </div>
              
              <div className="col-4">
                <Button 
                  variant="outline-secondary" 
                  className="w-100 py-3 d-flex flex-column align-items-center"
                  onClick={() => {
                    const checkItems = prompt('Ingresa ítems separados por comas:');
                    if (checkItems) {
                      const items = checkItems.split(',').map(item => ({
                        id: `check_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        text: item.trim(),
                        checked: false
                      }));
                      
                      handleSendContentMessage(
                        'Lista de verificación:',
                        'checkbox',
                        { checkboxItems: items }
                      );
                    }
                  }}
                >
                  <CheckSquare size={24} className="mb-2" />
                  <span>Checkboxes</span>
                </Button>
              </div>
              
              <div className="col-4">
                <Button 
                  variant="outline-secondary" 
                  className="w-100 py-3 d-flex flex-column align-items-center"
                  onClick={() => setShowFormBuilder(true)}
                >
                  <Award size={24} className="mb-2" />
                  <span>Formulario</span>
                </Button>
              </div>
            </div>
            
            <div className="p-3 border rounded">
              <h6>Comandos Rápidos</h6>
              <p className="mb-1 small text-muted">Puedes usar estos comandos en el chat:</p>
              <ul className="mb-0 small">
                <li><code>/titulo Tu Texto</code> - Crea un título</li>
                <li><code>/lista ítem1, ítem2, ítem3</code> - Crea una lista</li>
              </ul>
            </div>
            
            {/* Modal Constructor de Listas */}
            <Modal show={showListBuilder} onHide={() => setShowListBuilder(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Crear Lista</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Título de la Lista</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Ingrese un título"
                      value={listTitle}
                      onChange={(e) => setListTitle(e.target.value)}
                    />
                  </Form.Group>
                  
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Elementos</h6>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={addListItem}
                      >
                        <Plus size={16} />
                      </Button>
                    </div>
                    
                    {listItems.map((item, index) => (
                      <div key={index} className="d-flex mb-2">
                        <Form.Control 
                          type="text" 
                          placeholder="Elemento de lista"
                          value={item}
                          onChange={(e) => updateListItem(index, e.target.value)}
                        />
                        <Button 
                          variant="outline-danger" 
                          className="ms-2"
                          onClick={() => removeListItem(index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowListBuilder(false)}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleCreateList}>
                  Crear Lista
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        )}
      </div>
      
      <div className="p-2 border-top">
        <div className="input-group">
          <Button 
            variant="outline-secondary"
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip size={18} />
          </Button>
          <Form.Control
            placeholder="Escribe un mensaje..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          />
          <Button 
            variant="primary"
            onClick={handleSendMessage}
            disabled={isLoading}
          >
            <Send size={18} />
          </Button>
        </div>
        <div className="mt-1 d-flex justify-content-between">
          <small className="text-muted">
            Usa /titulo o /lista para crear contenido especial
          </small>
          <Button 
            variant="link" 
            size="sm" 
            className="p-0 text-decoration-none"
            onClick={() => {
              const systemMessage: ChatMessage = {
                id: `msg_${Date.now()}_system`,
                poiId: poi.id,
                content: 'Todos los datos de este punto han sido guardados correctamente.',
                timestamp: new Date(),
                sender: 'system',
                contentType: 'text'
              };
              setMessages(prev => [...prev, systemMessage]);
              chatService.addMessage(systemMessage);
            }}
          >
            <Save size={16} className="me-1" />
            <small>Guardar</small>
          </Button>
        </div>
      </div>
    </div>
  );
};

const Chart: React.FC<ChartProps> = ({ type, data, className = '', height = 250 }) => {
  // Referencia al contenedor del gráfico
  const chartRef = useRef<HTMLDivElement>(null);
  
  // Renderizar el gráfico
  useEffect(() => {
    if (!chartRef.current) return;
    
    // Aquí se implementaría la lógica para renderizar un gráfico real
    // usando una biblioteca como Chart.js, Recharts, D3, etc.
    // Por ahora, solo mostramos un placeholder
  }, [data, type, chartRef]);
  
  return (
    <div ref={chartRef} className={`chart-container ${className}`} style={{ height }}>
      <div className="d-flex flex-column h-100 w-100 justify-content-center align-items-center text-muted">
        {type === 'line' && <LineChart size={40} />}
        {type === 'bar' && <BarChart3 size={40} />}
        {type === 'pie' && <PieChart size={40} />}
        {type === 'area' && <AreaChart size={40} />}
        <div className="mt-2">{type.charAt(0).toUpperCase() + type.slice(1)} Chart</div>
        <div className="small text-muted">
          {data.labels && Array.isArray(data.labels) 
            ? `Labels: ${data.labels.join(', ')}`
            : 'Sin datos'}
        </div>
      </div>
    </div>
  );
};

// Componente para mostrar estadísticas de chat
const ChatStats: React.FC<{pois: POI[]}> = ({ pois }) => {
  // Calcular estadísticas de chat
  const totalChats = pois.filter(poi => poi.chatMessages && poi.chatMessages.length > 0).length;
  const totalMessages = pois.reduce((sum, poi) => sum + (poi.chatMessages?.length || 0), 0);
  const mostActivePoiId = pois.reduce((maxPoi, poi) => {
    const currentMessages = poi.chatMessages?.length || 0;
    const maxMessages = maxPoi.chatMessages?.length || 0;
    return currentMessages > maxMessages ? poi : maxPoi;
  }, pois[0] || {id: '', name: 'Ninguno', chatMessages: []});
  
  // Tipos de contenido
  const contentTypes = pois.flatMap(poi => poi.chatMessages || [])
    .reduce((types, msg) => {
      types[msg.contentType] = (types[msg.contentType] || 0) + 1;
      return types;
    }, {} as Record<string, number>);
  
  return (
    <Card className="h-100">
      <Card.Header className="p-3">
        <h5 className="mb-0">Estadísticas de Chat</h5>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col md={6} className="mb-3">
            <div className="border rounded p-3">
              <h6 className="mb-2">Actividad General</h6>
              <div className="mb-2">
                <strong>Puntos con chat:</strong> {totalChats} de {pois.length}
              </div>
              <div className="mb-2">
                <strong>Total mensajes:</strong> {totalMessages}
              </div>
              <div>
                <strong>Punto más activo:</strong> {mostActivePoiId.name} ({mostActivePoiId.chatMessages?.length || 0} mensajes)
              </div>
            </div>
          </Col>
          <Col md={6} className="mb-3">
            <div className="border rounded p-3">
              <h6 className="mb-2">Tipos de Contenido</h6>
              {Object.entries(contentTypes).length > 0 ? (
                Object.entries(contentTypes).map(([type, count]) => (
                  <div key={type} className="mb-1">
                    <strong>{type}:</strong> {count} mensajes
                  </div>
                ))
              ) : (
                <div className="text-muted">No hay datos de contenido</div>
              )}
            </div>
          </Col>
        </Row>
        <div className="border rounded p-3">
          <h6 className="mb-2">Actividad Reciente</h6>
          {pois.flatMap(poi => (poi.chatMessages || []).map(msg => ({...msg, poiName: poi.name})))
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
            .slice(0, 5)
            .map((msg) => (
              <div key={msg.id} className="mb-2 p-2 border-bottom">
                <div className="d-flex justify-content-between">
                  <span><strong>{msg.poiName}</strong>: {msg.content.substring(0, 30)}{msg.content.length > 30 ? '...' : ''}</span>
                  <small className="text-muted">{new Date(msg.timestamp).toLocaleString()}</small>
                </div>
                <small className="text-primary">{msg.contentType}</small>
              </div>
            ))}
          {pois.flatMap(poi => poi.chatMessages || []).length === 0 && (
            <div className="text-center text-muted">
              No hay actividad de chat reciente
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

const IconWrapper: React.FC<IconWrapperProps> = ({ 
  icon: Icon, 
  className = "", 
  size = 16,
  color 
}) => {
  return (
    <div 
      className={`d-inline-flex align-items-center justify-content-center ${className}`} 
      style={{ width: size, height: size }}
    >
      <Icon size={size} color={color} />
    </div>
  );
};

const GISTools: React.FC<GISToolsProps> = ({ tools, onSelect, activeToolId }) => {
  return (
    <Card className="h-100 bg-dark">
      <Card.Header className="p-3 d-flex justify-content-between align-items-center border-bottom border-secondary">
        <h5 className="mb-0 text-white">Herramientas GIS</h5>
        <Form.Control 
          type="text" 
          placeholder="Buscar herramienta..." 
          size="sm" 
          className="w-50 bg-dark text-white border-secondary"
        />
      </Card.Header>
      <Card.Body className="p-0">
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {tools.map(tool => (
            <div 
              key={tool.id}
              className={`p-3 border-bottom border-secondary d-flex align-items-center ${tool.id === activeToolId ? 'bg-secondary' : ''}`}
              onClick={() => onSelect(tool.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="me-3 position-relative">
                <div className="rounded p-2 text-body-tertiary" style={{ width: 40, height: 40 }}>
                  {tool.category === 'analysis' && <IconWrapper icon={Search} className="text-primary" size={20} />}
                  {tool.category === 'drawing' && <IconWrapper icon={Pencil} className="text-info" size={20} />}
                  {tool.category === 'measurement' && <IconWrapper icon={Ruler} className="text-success" size={20} />}
                  {tool.category === 'import' && <IconWrapper icon={DownloadCloud} className="text-warning" size={20} />}
                  {tool.category === 'export' && <IconWrapper icon={DownloadCloud} className="text-danger" size={20} />}
                  {tool.category === 'other' && <IconWrapper icon={Settings} className="text-secondary" size={20} />}
                </div>
                <div 
                  className={`position-absolute top-0 end-0 rounded-circle ${
                    tool.status === 'running' ? 'bg-success' : 
                    tool.status === 'error' ? 'bg-danger' : 'bg-secondary'
                  }`} 
                  style={{ width: 10, height: 10 }}
                ></div>
              </div>
              <div className="flex-grow-1">
                <h6 className="mb-0 fw-semibold text-white">{tool.name}</h6>
                <small className="text-muted">{tool.description}</small>
              </div>
              <ChevronRight size={16} className="text-muted" />
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

const POIsList: React.FC<POIsListProps> = ({ points, onSelect, activePoiId }) => {
  return (
    <Card className="h-100 bg-dark">
      <Card.Header className="p-3 d-flex justify-content-between align-items-center border-bottom border-secondary">
        <h5 className="mb-0 text-white">Puntos de Interés</h5>
        <Form.Control 
          type="text" 
          placeholder="Buscar punto..." 
          size="sm" 
          className="w-50 bg-dark text-white border-secondary"
        />
      </Card.Header>
      <Card.Body className="p-0">
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {points.map(point => (
            <div 
              key={point.id}
              className={`p-3 border-bottom border-secondary d-flex align-items-center ${point.id === activePoiId ? 'bg-secondary' : ''}`}
              onClick={() => onSelect(point.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="me-3 position-relative">
                <div className="rounded p-2 bg-secondary" style={{ width: 40, height: 40 }}>
                  {point.type === 'emergency' && <IconWrapper icon={Ambulance} className="text-danger" size={20} />}
                  {point.type === 'school' && <IconWrapper icon={School} className="text-success" size={20} />}
                  {point.type === 'commercial' && <IconWrapper icon={Building} className="text-warning" size={20} />}
                  {point.type === 'landmark' && <IconWrapper icon={MapPin} className="text-primary" size={20} />}
                  {point.type === 'custom' && <IconWrapper icon={MapPin} className="text-secondary" size={20} />}
                </div>
                <div 
                  className={`position-absolute top-0 end-0 rounded-circle ${
                    point.status === 'active' ? 'bg-success' : 
                    point.status === 'warning' ? 'bg-warning' : 'bg-danger'
                  }`} 
                  style={{ width: 10, height: 10 }}
                ></div>
              </div>
              <div className="flex-grow-1">
                <h6 className="mb-0 fw-semibold text-white">{point.name}</h6>
                <small className="text-muted">{point.metadata.address || `[${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}]`}</small>
              </div>
              <ChevronRight size={16} className="text-muted" />
            </div>
          ))}
        </div>
      </Card.Body>
    </Card>
  );
};

const GeoAlertsList: React.FC<GeoAlertsListProps> = ({ alerts }) => {
  const getAlertVariant = (severity: GeoAlert['severity']): string => {
    switch (severity) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  const getAlertIcon = (type: GeoAlert['type']): React.ReactNode => {
    switch (type) {
      case 'boundary': return <IconWrapper icon={Shield} size={18} />;
      case 'proximity': return <IconWrapper icon={MapPin} size={18} />;
      case 'environmental': return <IconWrapper icon={AlertTriangle} size={18} />;
      case 'traffic': return <IconWrapper icon={Activity} size={18} />;
      case 'security': return <IconWrapper icon={Lock} size={18} />;
      case 'custom': return <IconWrapper icon={Globe} size={18} />;
      default: return <IconWrapper icon={AlertTriangle} size={18} />;
    }
  };

  const getTimeAgo = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  const activeAlerts = alerts.filter(alert => !alert.resolved);

  return (
    <Card className="h-100 bg-dark">
      <Card.Header className="p-3 d-flex justify-content-between align-items-center border-bottom border-secondary">
        <h5 className="mb-0 text-white">Alertas Geográficas</h5>
        <Button variant="outline-light" size="sm" className="p-1">
          <IconWrapper icon={RefreshCw} />
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        {activeAlerts.length === 0 ? (
          <div className="text-center p-4 text-muted">
            <IconWrapper icon={Shield} size={32} className="mb-2 text-secondary" />
            <p>No hay alertas activas en este momento</p>
          </div>
        ) : (
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {activeAlerts.map(alert => (
              <div 
                key={alert.id} 
                className="p-3 border-bottom border-secondary d-flex align-items-start"
              >
                <div className={`me-3 text-${getAlertVariant(alert.severity)}`}>
                  {getAlertIcon(alert.type)}
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between mb-1">
                    <h6 className="mb-0 text-white">{alert.message}</h6>
                    <Badge bg={getAlertVariant(alert.severity)} className="text-uppercase">
                      {alert.severity}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between small text-muted">
                    <span>
                      [{alert.location.lat.toFixed(4)}, {alert.location.lng.toFixed(4)}]
                      {alert.radius && ` • Radio: ${alert.radius}m`}
                    </span>
                    <span className="d-flex align-items-center">
                      <IconWrapper icon={Clock} size={12} className="me-1" /> 
                      {getTimeAgo(alert.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// Componente para las gráficas en la sección inferior
const ActivityCharts: React.FC = () => {
  // Acceder a los datos desde el contexto superior
  const dashboardContext = useContext(XGISDashboardContext);
  const { historicalData, chartPeriod, setChartPeriod, pois, zones } = dashboardContext;
  
  // Preparar datos para gráficas
  const labels = historicalData.map(d => d.date);
  const poisData = historicalData.map(d => d.pois);
  const alertsData = historicalData.map(d => d.alerts);
  const plumesData = historicalData.map(d => d.plumes);
  const zonesData = historicalData.map(d => d.zones);

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="p-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Actividad del Sistema</h5>
          <div className="d-flex gap-2 align-items-center">
            <div className="d-flex align-items-center me-3">
              <IconWrapper icon={Calendar} className="me-2" />
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm" id="period-dropdown">
                  {chartPeriod === 'day' ? 'Hoy' : 
                   chartPeriod === 'week' ? 'Esta semana' : 'Este mes'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setChartPeriod('day')}>Hoy</Dropdown.Item>
                  <Dropdown.Item onClick={() => setChartPeriod('week')}>Esta semana</Dropdown.Item>
                  <Dropdown.Item onClick={() => setChartPeriod('month')}>Este mes</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <Button variant="outline-secondary" size="sm">
              <IconWrapper icon={RefreshCw} className="me-1" /> Actualizar
            </Button>
            <Button variant="outline-secondary" size="sm">
              <IconWrapper icon={Filter} className="me-1" /> Filtrar
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xl={6} className="mb-3">
              <h6 className="mb-3">Evolución de Puntos de Interés y Alertas</h6>
              <Chart 
                type="line" 
                data={{
                  labels,
                  datasets: [
                    {
                      label: 'POIs',
                      data: poisData,
                      borderColor: '#3388ff',
                    },
                    {
                      label: 'Alertas',
                      data: alertsData,
                      borderColor: '#ff3333',
                    }
                  ]
                }}
              />
            </Col>
            <Col xl={6} className="mb-3">
              <h6 className="mb-3">Distribución de Tipos de Zonas</h6>
              <Chart 
                type="pie" 
                data={{
                  labels: ['Área', 'Ruta', 'Emergencia', 'Dispersión', 'Personalizada'],
                  datasets: [
                    {
                      data: [
                        zones.filter(z => z.type === 'area').length,
                        zones.filter(z => z.type === 'route').length,
                        zones.filter(z => z.type === 'emergency').length,
                        zones.filter(z => z.type === 'dispersion').length,
                        zones.filter(z => z.type === 'custom').length
                      ],
                      backgroundColor: [
                        '#3388ff',
                        '#33cc33',
                        '#ff3333',
                        '#ff33ff',
                        '#999999'
                      ]
                    }
                  ]
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col xl={6} className="mb-3">
              <h6 className="mb-3">Estado de Puntos de Interés</h6>
              <Chart 
                type="bar" 
                data={{
                  labels: ['Activos', 'Advertencia', 'Inactivos'],
                  datasets: [
                    {
                      data: [
                        pois.filter(p => p.status === 'active').length,
                        pois.filter(p => p.status === 'warning').length,
                        pois.filter(p => p.status === 'inactive').length
                      ],
                      backgroundColor: [
                        '#33cc33',
                        '#ffcc33',
                        '#ff3333'
                      ]
                    }
                  ]
                }}
              />
            </Col>
            <Col xl={6} className="mb-3">
              <h6 className="mb-3">Actividad de Plumas y Zonas</h6>
              <Chart 
                type="area" 
                data={{
                  labels,
                  datasets: [
                    {
                      label: 'Plumas',
                      data: plumesData,
                      backgroundColor: 'rgba(255, 51, 255, 0.3)',
                      borderColor: '#ff33ff',
                    },
                    {
                      label: 'Zonas',
                      data: zonesData,
                      backgroundColor: 'rgba(51, 136, 255, 0.3)',
                      borderColor: '#3388ff',
                    }
                  ]
                }}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="p-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Actividad Reciente</h5>
          <Button variant="outline-secondary" size="sm">
            Ver todas las actividades
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive>
            <thead>
              <tr>
                <th>Evento</th>
                <th>Ubicación</th>
                <th>Usuario</th>
                <th>Fecha</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Creación de pluma de dispersión</td>
                <td>[-33.4372, -70.6506]</td>
                <td>Sistema</td>
                <td>{new Date().toLocaleString()}</td>
                <td><Badge bg="success">Completado</Badge></td>
              </tr>
              <tr>
                <td>Modificación de zona</td>
                <td>Área Metropolitana</td>
                <td>juan.perez</td>
                <td>{new Date(Date.now() - 3600000).toLocaleString()}</td>
                <td><Badge bg="success">Completado</Badge></td>
              </tr>
              <tr>
                <td>Análisis de proximidad</td>
                <td>Hospital Central</td>
                <td>maria.lopez</td>
                <td>{new Date(Date.now() - 7200000).toLocaleString()}</td>
                <td><Badge bg="info">En proceso</Badge></td>
              </tr>
              <tr>
                <td>Importación GeoJSON</td>
                <td>--</td>
                <td>admin</td>
                <td>{new Date(Date.now() - 86400000).toLocaleString()}</td>
                <td><Badge bg="success">Completado</Badge></td>
              </tr>
              <tr>
                <td>Cálculo de rutas</td>
                <td>Múltiples puntos</td>
                <td>carlos.rodriguez</td>
                <td>{new Date(Date.now() - 172800000).toLocaleString()}</td>
                <td><Badge bg="danger">Error</Badge></td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};

const MapInitializer: React.FC<MapInitializerProps> = ({ onMapReady }) => {
  const map = useMap();
  
  useEffect(() => {
    onMapReady(map);
  }, [map, onMapReady]);
  
  return null;
};

// Componente principal
const XGISDashboard: React.FC = () => {
  // Configuración inicial
  const initialCenter: [number, number] = [-33.4489, -70.6693]; // Santiago, Chile
  const initialZoom = 10;
  
  // Datos iniciales para herramientas GIS
  const initialGisTools: GISTool[] = [
    {
      id: 'tool_1',
      name: 'Análisis de Proximidad',
      category: 'analysis',
      status: 'stopped',
      lastRun: new Date(Date.now() - 86400000),
      description: 'Analiza la proximidad entre puntos de interés seleccionados',
      command: 'analyze proximity --radius=2000 --points=[id1,id2,id3]'
    },
    {
      id: 'tool_2',
      name: 'Medir Distancia',
      category: 'measurement',
      status: 'stopped',
      lastRun: new Date(Date.now() - 172800000),
      description: 'Mide la distancia entre puntos seleccionados',
      command: 'measure distance --from=point1 --to=point2'
    },
    {
      id: 'tool_3',
      name: 'Exportar GeoJSON',
      category: 'export',
      status: 'running',
      lastRun: new Date(),
      description: 'Exporta elementos seleccionados a formato GeoJSON',
      command: 'export geojson --elements=[zone1,point2,point3]'
    },
    {
      id: 'tool_4',
      name: 'Dibujar Polígono',
      category: 'drawing',
      status: 'stopped',
      lastRun: new Date(Date.now() - 259200000),
      description: 'Habilita la herramienta de dibujo de polígonos',
      command: 'enable drawing --type=polygon'
    },
  ];

  // Datos iniciales para POIs con chatMessages vacío
  const initialPOIs: POI[] = [
    {
      id: 'poi_1',
      name: 'Hospital Central',
      lat: -33.4372,
      lng: -70.6506,
      type: 'emergency',
      status: 'active',
      lastUpdate: new Date(),
      metadata: { address: 'Av. Libertador 1200', capacity: 350 },
      chatMessages: []
    },
    {
      id: 'poi_2',
      name: 'Escuela República',
      lat: -33.4489,
      lng: -70.6693,
      type: 'school',
      status: 'active',
      lastUpdate: new Date(Date.now() - 86400000),
      metadata: { address: 'Calle Educación 500', students: 1200 },
      chatMessages: []
    },
    {
      id: 'poi_3',
      name: 'Centro Comercial',
      lat: -33.4572,
      lng: -70.6354,
      type: 'commercial',
      status: 'warning',
      lastUpdate: new Date(Date.now() - 43200000),
      metadata: { address: 'Av. Comercio 800', stores: 120 },
      chatMessages: []
    }
  ];

  // Estados con datos iniciales
  const [gisTools, setGisTools] = useState<GISTool[]>(initialGisTools);
  const [pois, setPois] = useState<POI[]>(initialPOIs);
  const [zones, setZones] = useState<Zone[]>([]);
  const [dispersionPlumes, setDispersionPlumes] = useState<DispersionPlume[]>([]);
  const [alerts, setAlerts] = useState<GeoAlert[]>([]);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>([]);
  const [activeTool, setActiveTool] = useState<string | undefined>();
  const [activePOI, setActivePOI] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState<'tools' | 'pois' | 'layers'>('tools');
  const [activeBasemap, setActiveBasemap] = useState<string>('streets');
  const [visibleLayers, setVisibleLayers] = useState<Record<string, boolean>>({
    points: true,
    zones: true,
    plumes: true,
    external: true,
  });
  const [aiCommand, setAiCommand] = useState<string>('');
  const [aiResponse, setAiResponse] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [externalData, setExternalData] = useState<any>(null);
  const [chartPeriod, setChartPeriod] = useState<'day' | 'week' | 'month'>('week');
  const [historicalData, setHistoricalData] = useState<HistoricalData[]>([]);
  
  // Nuevo estado para chat
  const [activeChatPOI, setActiveChatPOI] = useState<POI | null>(null);
  const [showChat, setShowChat] = useState<boolean>(false);

  // Referencias
  const mapRef = useRef<L.Map | null>(null);
  const pointLayersRef = useRef<L.LayerGroup | null>(null);
  const zoneLayersRef = useRef<L.LayerGroup | null>(null);
  const plumeLayersRef = useRef<L.LayerGroup | null>(null);
  const externalLayersRef = useRef<L.LayerGroup | null>(null);
  const drawControlRef = useRef<any>(null);
  const drawLayerRef = useRef<L.FeatureGroup | null>(null);

  // Crear el valor del contexto para compartir entre componentes
  const contextValue = {
    pois,
    zones,
    dispersionPlumes,
    alerts,
    historicalData,
    chartPeriod,
    setChartPeriod,
    gisTools,
    activeTool,
    activePOI,
    activeTab,
    activeBasemap,
    visibleLayers,
    mapRef,
    pointLayersRef,
    zoneLayersRef,
    plumeLayersRef,
    externalLayersRef,
    activeChatPOI,
    showChat
  };

  // Utility function para generar alertas de demo
  const generateRandomAlerts = useCallback((count: number): GeoAlert[] => {
    const alertTypes: Array<GeoAlert['type']> = ['boundary', 'proximity', 'environmental', 'traffic', 'security', 'custom'];
    const severityLevels: Array<GeoAlert['severity']> = ['critical', 'high', 'medium', 'low', 'info'];
    
    const alertMessages: Record<GeoAlert['type'], string[]> = {
      boundary: [
        'Alerta de perímetro traspasado',
        'Vehículo salió del área designada',
        'Dispositivo detectado fuera de zona autorizada',
        'Entrada no autorizada en zona restringida'
      ],
      proximity: [
        'Proximidad a zona de riesgo detectada',
        'Acercamiento a área de exclusión',
        'Distancia crítica a material peligroso',
        'Dispositivos en cercanía no autorizada'
      ],
      environmental: [
        'Alerta de calidad del aire crítica',
        'Temperatura por encima de umbral',
        'Nivel de ruido excesivo detectado',
        'Contaminación detectada en curso de agua'
      ],
      traffic: [
        'Congestión severa detectada',
        'Velocidad promedio bajo nivel crítico',
        'Tráfico parado en acceso principal',
        'Aumento anormal de densidad vehicular'
      ],
      security: [
        'Actividad sospechosa detectada',
        'Aglomeración no programada detectada',
        'Movimiento inusual en horario restringido',
        'Posible intrusión detectada'
      ],
      custom: [
        'Evento planificado iniciado',
        'Proceso de emergencia activado',
        'Protocolo de seguridad iniciado',
        'Sincronización completada'
      ]
    };
    
    // Generar coordenadas aleatorias cerca del centro
    const generateRandomLocation = (): {lat: number, lng: number} => {
      return {
        lat: initialCenter[0] + (Math.random() * 0.2 - 0.1),
        lng: initialCenter[1] + (Math.random() * 0.2 - 0.1)
      };
    };
    
    return Array(count).fill(0).map((_, index) => {
      const type = alertTypes[Math.floor(Math.random() * alertTypes.length)];
      const severity = severityLevels[Math.floor(Math.random() * severityLevels.length)];
      const location = generateRandomLocation();
      const timestamp = new Date(Date.now() - Math.floor(Math.random() * 86400000));
      const messageList = alertMessages[type];
      const message = messageList[Math.floor(Math.random() * messageList.length)];
      
      return {
        id: `alert_${Date.now()}_${index}`,
        type,
        severity,
        location,
        radius: Math.floor(Math.random() * 1000) + 100,
        timestamp,
        message,
        resolved: Math.random() > 0.7,
      };
    });
  }, [initialCenter]);
  
  // Función para generar datos históricos de muestra
  const generateHistoricalData = useCallback((period: 'day' | 'week' | 'month'): HistoricalData[] => {
    const now = new Date();
    const data: HistoricalData[] = [];
    
    let days = 0;
    switch (period) {
      case 'day':
        days = 24; // 24 horas
        break;
      case 'week':
        days = 7; // 7 días
        break;
      case 'month':
      default:
        days = 30; // 30 días
        break;
    }
    
    for (let i = 0; i < days; i++) {
      const date = new Date();
      
      if (period === 'day') {
        date.setHours(now.getHours() - i);
        date.setMinutes(0);
        date.setSeconds(0);
      } else {
        date.setDate(now.getDate() - i);
      }
      
      // Crear una tendencia general con variación aleatoria
      const baseline = 50 - i * 0.5; // Tendencia creciente hacia el presente
      
      data.push({
        date: period === 'day' 
          ? date.toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit' }) 
          : date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
        pois: Math.floor(baseline + Math.random() * 10),
        alerts: Math.floor((baseline * 0.6) + Math.random() * 15),
        plumes: Math.floor((baseline * 0.2) + Math.random() * 5),
        zones: Math.floor((baseline * 0.4) + Math.random() * 8)
      });
    }
    
    return data.reverse(); // Ordenar cronológicamente ascendente
  }, []);

  // Utility function para obtener estadísticas
  const getStats = useCallback(() => {
    return {
      totalAlerts: alerts.length,
      criticalAlerts: alerts.filter(a => a.severity === 'critical' && !a.resolved).length,
      activePOIs: pois.filter(p => p.status === 'active').length,
      warningPOIs: pois.filter(p => p.status === 'warning').length,
      activeZones: zones.length,
      toolsRunning: gisTools.filter(t => t.status === 'running').length,
      analysesCount: analysisResults.length
    };
  }, [alerts, pois, zones, gisTools, analysisResults]);

  // Método más controlado para generar alertas con tipado correcto
  const generateControlledAlerts = (): GeoAlert | null => {
    // Genera alertas con más control
    if (Math.random() < 0.1) {  // 10% de probabilidad
      const possibleAlertTypes: GeoAlert['type'][] = [
        'boundary', 
        'proximity', 
        'environmental', 
        'traffic', 
        'security', 
        'custom'
      ];

      const newAlert: GeoAlert = {
        id: `alert_${Date.now()}`,  // Identificador único basado en timestamp
        type: possibleAlertTypes[Math.floor(Math.random() * possibleAlertTypes.length)],
        severity: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)] as GeoAlert['severity'],
        timestamp: new Date(),
        location: {
          lat: -33.4372 + (Math.random() - 0.5) * 0.2,  // Variación cerca de Santiago
          lng: -70.6506 + (Math.random() - 0.5) * 0.2
        },
        message: 'Alerta generada automáticamente',
        resolved: false
      };

      return newAlert;
    }
    return null;
  };
  
  // En el useEffect
  useEffect(() => {
    // Generar algunas alertas iniciales
    setAlerts(generateRandomAlerts(3));
    
    const interval = setInterval(() => {
      // Generación más controlada de alertas
      const newAlert = generateControlledAlerts();
      if (newAlert) {
        setAlerts(prev => {
          // Limitar a 10 alertas, evitar duplicados
          const updatedAlerts = [newAlert, ...prev.filter(a => a.id !== newAlert.id)].slice(0, 10);
          return updatedAlerts;
        });
      }
  
      // FIX: Verificar que gisTools tenga elementos antes de intentar actualizarlos
      if (gisTools.length > 0) {
        setGisTools(prev => {
          const newTools = [...prev];
          const randomIndex = Math.floor(Math.random() * newTools.length);
          // Verificar que el índice sea válido
          if (randomIndex >= 0 && randomIndex < newTools.length) {
            newTools[randomIndex] = {
              ...newTools[randomIndex],
              status: newTools[randomIndex].status === 'running' ? 'stopped' : 'running',
              lastRun: newTools[randomIndex].status === 'stopped' ? new Date() : newTools[randomIndex].lastRun
            };
          }
          return newTools;
        });
      }
    }, 60000);  // Cada minuto
  
    return () => clearInterval(interval);
  }, []);

  // Actualizar datos históricos cuando cambia el período
  useEffect(() => {
    setHistoricalData(generateHistoricalData(chartPeriod));
  }, [chartPeriod, generateHistoricalData]);

  // Manejar mensaje de chat para un POI
  const handlePoiMessage = (poiId: string, content: string, contentType: ChatMessage['contentType'], metadata?: any) => {
    // Añadir mensaje al POI correspondiente
    setPois(prevPOIs => 
      prevPOIs.map(poi => {
        if (poi.id === poiId) {
          const newMessage: ChatMessage = {
            id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            poiId,
            content,
            timestamp: new Date(),
            sender: 'user',
            contentType,
            metadata
          };
          
          // Asegurarse de que chatMessages exista
          const currentMessages = poi.chatMessages || [];
          return {
            ...poi,
            chatMessages: [...currentMessages, newMessage]
          };
        }
        return poi;
      })
    );
    
    // Si hay un chat activo, actualizarlo también
    if (activeChatPOI && activeChatPOI.id === poiId) {
      const updatedPoi = pois.find(p => p.id === poiId);
      if (updatedPoi) {
        setActiveChatPOI(updatedPoi);
      }
    }
  };

  // Calcular pluma de dispersión
  const calculateDispersionPlume = (data: {
    source: { lat: number; lng: number };
    direction: number;
    speed: number;
    concentration: number;
  }) => {
    try {
      const { source, direction, speed, concentration } = data;
      
      // Crear una forma de cono simple
      const distance = speed * 1000; // Convertir a metros
      const spreadAngle = 30; // grados
      
      // Crear puntos para la forma de cono
      const directionRad = (direction * Math.PI) / 180;
      
      // Crear coordenadas para la pluma
      const coordinates: [number, number][] = [];
      coordinates.push([source.lng, source.lat]); // Empezar con el punto de origen
      
      // Calcular puntos a lo largo del arco
      for (let angle = directionRad - (spreadAngle * Math.PI / 180); 
           angle <= directionRad + (spreadAngle * Math.PI / 180); 
           angle += 0.1) {
        const x = source.lng + (distance / 111320) * Math.sin(angle);
        const y = source.lat + (distance / 111320) * Math.cos(angle);
        coordinates.push([x, y]);
      }
      
      coordinates.push([source.lng, source.lat]); // Cerrar el polígono
      
      // Crear una nueva pluma de dispersión
      const newPlume: DispersionPlume = {
        id: `plume_${Date.now()}`,
        source,
        direction,
        speed,
        concentration,
        coordinates: [coordinates],
      };
      
      setDispersionPlumes(prevPlumes => [...prevPlumes, newPlume]);
      
      // Actualizar mapa solo si las referencias existen
      if (plumeLayersRef.current && mapRef.current) {
        try {
          const polygon = L.polygon(
            coordinates.map(coord => [coord[1], coord[0]]) as L.LatLngExpression[],
            {
              color: '#ff00ff',
              fillOpacity: 0.4 * (concentration / 100), // Opacidad basada en concentración
              fillColor: '#ff00ff',
              weight: 1,
            }
          ).bindPopup(
            `<div>
              <h4>Pluma de Dispersión</h4>
              <p>Origen: [${source.lat.toFixed(4)}, ${source.lng.toFixed(4)}]</p>
              <p>Dirección del Viento: ${direction}°</p>
              <p>Velocidad del Viento: ${speed} m/s</p>
              <p>Concentración: ${concentration}%</p>
            </div>`
          );
          
          polygon.addTo(plumeLayersRef.current);
          
          // Ajustar vista al área de la pluma si es necesario
          if (mapRef.current) {
            mapRef.current.fitBounds(polygon.getBounds());
          }
        } catch (error) {
          console.error('Error añadiendo polígono al mapa:', error);
        }
      }
      
      return true;  // Éxito
    } catch (error) {
      console.error('Error calculando pluma de dispersión:', error);
      return false;  // Fallo
    }
  };

  // Inicialización del mapa y herramientas de dibujo
  const handleMapReady = (map: L.Map) => {
    mapRef.current = map;
    
    try {
      // Inicializar capas
      pointLayersRef.current = L.layerGroup().addTo(map);
      zoneLayersRef.current = L.layerGroup().addTo(map);
      plumeLayersRef.current = L.layerGroup().addTo(map);
      externalLayersRef.current = L.layerGroup().addTo(map);
      
      // Inicializar capa de dibujo
      drawLayerRef.current = L.featureGroup().addTo(map);
      
      // Configurar herramientas de dibujo
      setupDrawTools(map);
      
      // Configurar eventos
      map.on('moveend', handleMapChange);
      
      // Visualizar POIs iniciales
      renderPOIs();
      renderZones();
      
      console.log('Mapa inicializado correctamente');
    } catch (error) {
      console.error('Error inicializando el mapa:', error);
    }
  };
  
  // Configurar herramientas de dibujo
  const setupDrawTools = (map: L.Map) => {
    if (!L.Draw) {
      console.error('Leaflet.Draw no está disponible');
      return;
    }
  
    // Crear grupo de capas para el dibujo si no existe
    if (!drawLayerRef.current) {
      drawLayerRef.current = new L.FeatureGroup();
      drawLayerRef.current.addTo(map);
    }
  
    // Configurar opciones de dibujo
    const drawOptions = {
      position: 'topright',
      draw: {
        polyline: {
          shapeOptions: {
            color: '#f357a1',
            weight: 3
          }
        },
        polygon: {
          allowIntersection: false,
          drawError: {
            color: '#e1e100',
            message: '<strong>Error:</strong> ¡Las formas no pueden intersectarse!'
          },
          shapeOptions: {
            color: '#3388ff'
          }
        },
        circle: false,
        rectangle: {
          shapeOptions: {
            clickable: true,
            color: '#33cc33'
          }
        },
        marker: true
      },
      edit: {
        featureGroup: drawLayerRef.current,
        remove: true
      }
    };
  
    // Crear y añadir el control de dibujo
    drawControlRef.current = new L.Control.Draw(drawOptions);
    map.addControl(drawControlRef.current);
  
    // Vincular eventos
    map.on(L.Draw.Event.CREATED, handleDrawCreated);
    map.on(L.Draw.Event.EDITED, handleDrawEdited);
    map.on(L.Draw.Event.DELETED, handleDrawDeleted);
  };

  // Manejar dibujo editado
  const handleDrawEdited = (e: any) => {
    const layers = e.layers;
    layers.eachLayer((layer: any) => {
      // Actualiza POIs o zonas basado en el tipo de capa
      if (layer instanceof L.Marker) {
        const latlng = layer.getLatLng();
        // Actualizar POI existente
        updatePOI(layer.id, { lat: latlng.lat, lng: latlng.lng });
      } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
        const latlngs = layer.getLatLngs()[0];
        // Convertir a formato GeoJSON
        const coordinates: [number, number][] = latlngs.map((latlng: L.LatLng) => [latlng.lng, latlng.lat]);
        
        // Asegurar que el polígono esté cerrado
        if (coordinates[0][0] !== coordinates[coordinates.length - 1][0] || 
            coordinates[0][1] !== coordinates[coordinates.length - 1][1]) {
          coordinates.push([coordinates[0][0], coordinates[0][1]]);
        }
        
        // Actualizar zona existente
        updateZone(layer.id, { coordinates: [coordinates] });
      }
    });
  };

  // Manejar dibujo eliminado
  const handleDrawDeleted = (e: any) => {
    const layers = e.layers;
    layers.eachLayer((layer: any) => {
      // Eliminar POIs o zonas basado en el tipo de capa
      if (layer instanceof L.Marker) {
        // Eliminar POI
        if (layer.id) {
          deletePOI(layer.id);
        }
      } else if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
        // Eliminar zona
        if (layer.id) {
          deleteZone(layer.id);
        }
      }
    });
  };

  // Añadir las funciones de actualización y eliminación
  const updatePOI = (id: string, updates: Partial<POI>) => {
    setPois(prevPOIs => 
      prevPOIs.map(poi => 
        poi.id === id ? { ...poi, ...updates } : poi
      )
    );
  };

  const deletePOI = (id: string) => {
    setPois(prevPOIs => prevPOIs.filter(poi => poi.id !== id));
  };

  const updateZone = (id: string, updates: Partial<Zone>) => {
    setZones(prevZones => 
      prevZones.map(zone => 
        zone.id === id ? { ...zone, ...updates } : zone
      )
    );
  };

  const deleteZone = (id: string) => {
    setZones(prevZones => prevZones.filter(zone => zone.id !== id));
  };

  // Visualizar POIs en el mapa
  const renderPOIs = () => {
    if (!pointLayersRef.current || !mapRef.current) return;
    
    // Limpiar capa existente
    pointLayersRef.current.clearLayers();
    
    // Añadir cada POI al mapa
    pois.forEach(poi => {
      const marker = L.marker([poi.lat, poi.lng], {
        icon: getPOIIcon(poi.type),
      }).bindPopup(`
        <div>
          <h4>${poi.name}</h4>
          <p>${poi.metadata.address || ''}</p>
          <p>Estado: ${poi.status === 'active' ? 'Activo' : poi.status === 'warning' ? 'Advertencia' : 'Inactivo'}</p>
          ${poi.lastUpdate ? `<p>Actualizado: ${poi.lastUpdate.toLocaleString()}</p>` : ''}
          <button class="btn btn-primary btn-sm mt-2 open-chat-btn" data-poi-id="${poi.id}">Abrir Chat</button>
        </div>
      `);
      
      // Asignar ID de POI al marcador
      (marker as any).poiId = poi.id;
      
      // Añadir evento para abrir chat
      marker.on('popupopen', () => {
        setTimeout(() => {
          const chatButton = document.querySelector(`.open-chat-btn[data-poi-id="${poi.id}"]`);
          if (chatButton) {
            chatButton.addEventListener('click', (e) => {
              e.preventDefault();
              const targetPoi = pois.find(p => p.id === poi.id);
              if (targetPoi) {
                setActiveChatPOI(targetPoi);
                setShowChat(true);
                mapRef.current?.closePopup();
              }
            });
          }
        }, 100);
      });
      
      marker.addTo(pointLayersRef.current!);
    });
  };
  
  // Renderizar zonas en el mapa
  const renderZones = () => {
    if (!zoneLayersRef.current || !mapRef.current) return;
    
    // Limpiar capa existente
    zoneLayersRef.current.clearLayers();
    
    // Añadir cada zona al mapa
    zones.forEach(zone => {
      const coords = zone.coordinates[0].map(coord => [coord[1], coord[0]]);
      const polygon = L.polygon(
        coords as L.LatLngExpression[],
        getZoneStyle(zone.type)
      ).bindPopup(`
        <div>
          <h4>${zone.name}</h4>
          <p>Tipo: ${zone.type}</p>
          ${zone.metadata ? `<p>${Object.entries(zone.metadata).map(([key, value]) => `${key}: ${value}`).join('<br>')}</p>` : ''}
        </div>
      `);
      
      polygon.addTo(zoneLayersRef.current!);
    });
  };

  // Manejar cambio en el mapa
  const handleMapChange = () => {
    if (mapRef.current) {
      console.log('Límites del mapa cambiados:', mapRef.current.getBounds());
    }
  };

  // Manejar dibujo creado
  const handleDrawCreated = (e: any) => {
    try {
      const { layerType, layer } = e;
      
      // Añadir capa al grupo de dibujo
      if (drawLayerRef.current) {
        drawLayerRef.current.addLayer(layer);
      }
      
      if (layerType === 'marker') {
        const latlng = layer.getLatLng();
        const newPoi: Omit<POI, 'id'> = {
          name: 'Nuevo Punto',
          lat: latlng.lat,
          lng: latlng.lng,
          type: 'custom',
          status: 'active',
          metadata: {},
          chatMessages: [] // Inicializar mensajes vacíos
        };
        
        addPOI(newPoi);
        
        // Opcional: abrir automáticamente el chat para el nuevo punto
        setTimeout(() => {
          const addedPoi = pois.find(p => 
            p.lat === latlng.lat && 
            p.lng === latlng.lng && 
            p.name === 'Nuevo Punto'
          );
          
          if (addedPoi) {
            setActiveChatPOI(addedPoi);
            setShowChat(true);
          }
        }, 500);
      } else if (layerType === 'polygon' || layerType === 'rectangle') {
        const latlngs = layer.getLatLngs()[0];
        
        // Convertir a formato GeoJSON
        const coordinates: [number, number][] = latlngs.map((latlng: L.LatLng) => [latlng.lng, latlng.lat]);
        
        // Asegurar que el polígono esté cerrado
        if (coordinates[0][0] !== coordinates[coordinates.length - 1][0] || 
          coordinates[0][1] !== coordinates[coordinates.length - 1][1]) {
        coordinates.push([coordinates[0][0], coordinates[0][1]]);
      }
      
      addZone({
        name: 'Nueva Zona',
        type: 'custom',
        coordinates: [coordinates],
        metadata: {},
      });
    }
  } catch (error) {
    console.error('Error al procesar elemento dibujado:', error);
  }
};

// Obtener estilo para zonas
const getZoneStyle = (type: Zone['type']) => {
  switch (type) {
    case 'area':
      return { color: '#3388ff', fillOpacity: 0.2, weight: 2 };
    case 'route':
      return { color: '#33cc33', fillOpacity: 0.1, weight: 4 };
    case 'emergency':
      return { color: '#ff3333', fillOpacity: 0.3, weight: 2 };
    case 'dispersion':
      return { color: '#ff33ff', fillOpacity: 0.4, weight: 2 };
    default:
      return { color: '#3388ff', fillOpacity: 0.2, weight: 2 };
  }
};

// Obtener icono para POI
const getPOIIcon = (type: POI['type']) => {
  let html = '';
  let color = '#3388ff';
  
  switch (type) {
    case 'emergency':
      color = '#ff3333';
      html = '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">+</div>';
      break;
    case 'school':
      color = '#33bb33';
      html = '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">S</div>';
      break;
    case 'commercial':
      color = '#ff9933';
      html = '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">C</div>';
      break;
    case 'landmark':
      color = '#3388ff';
      html = '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">L</div>';
      break;
    default:
      color = '#3388ff';
      html = '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">•</div>';
  }
  
  return L.divIcon({
    html: `<div style="background-color: ${color}; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white;">${html}</div>`,
    className: 'custom-div-icon',
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });
};

// Cargar datos externos desde GeoJSON
const fetchExternalData = async (url: string) => {
  if (!url || !url.trim()) {
    setAiResponse("Por favor, proporciona una URL válida para cargar datos GeoJSON.");
    return;
  }
  
  setLoading(true);
  try {
    const response = await axios.get(url);
    
    if (response.data && response.status === 200) {
      processExternalGeoJSON(response.data);
      setAiResponse(`Datos GeoJSON cargados exitosamente desde ${url}`);
    } else {
      throw new Error(`Error en respuesta: ${response.status}`);
    }
  } catch (error) {
    console.error('Error cargando datos externos:', error);
    setAiResponse(`Error al cargar datos: ${error instanceof Error ? error.message : String(error)}`);
  } finally {
    setLoading(false);
  }
};

// Procesar datos GeoJSON externos
const processExternalGeoJSON = (geojson: any) => {
  if (!externalLayersRef.current || !mapRef.current) return;
  
  externalLayersRef.current.clearLayers();
  
  L.geoJSON(geojson, {
    style: (feature) => {
      return {
        color: feature?.properties?.color || '#3388ff',
        weight: feature?.properties?.weight || 3,
        opacity: feature?.properties?.opacity || 0.7,
        fillOpacity: feature?.properties?.fillOpacity || 0.2,
      };
    },
    pointToLayer: (feature, latlng) => {
      const icon = L.divIcon({
        html: `<div style="background-color: ${feature.properties?.color || '#3388ff'}; 
              width: 12px; height: 12px; border-radius: 50%; border: 2px solid white;"></div>`,
        className: 'custom-div-icon',
      });
      return L.marker(latlng, { icon });
    },
    onEachFeature: (feature, layer) => {
      if (feature.properties) {
        layer.bindPopup(
          `<div>
            <h4>${feature.properties.name || 'Datos Externos'}</h4>
            <p>${feature.properties.description || ''}</p>
          </div>`
        );
      }
    },
  }).addTo(externalLayersRef.current);
};

// Manejar cambio de visibilidad de capas
const toggleLayerVisibility = (layerName: keyof typeof visibleLayers) => {
  setVisibleLayers(prev => {
    const newVisibility = { ...prev, [layerName]: !prev[layerName] };
    
    if (mapRef.current) {
      if (layerName === 'points' && pointLayersRef.current) {
        if (newVisibility.points) {
          pointLayersRef.current.addTo(mapRef.current);
        } else {
          pointLayersRef.current.removeFrom(mapRef.current);
        }
      }
      
      if (layerName === 'zones' && zoneLayersRef.current) {
        if (newVisibility.zones) {
          zoneLayersRef.current.addTo(mapRef.current);
        } else {
          zoneLayersRef.current.removeFrom(mapRef.current);
        }
      }
      
      if (layerName === 'plumes' && plumeLayersRef.current) {
        if (newVisibility.plumes) {
          plumeLayersRef.current.addTo(mapRef.current);
        } else {
          plumeLayersRef.current.removeFrom(mapRef.current);
        }
      }
      
      if (layerName === 'external' && externalLayersRef.current) {
        if (newVisibility.external) {
          externalLayersRef.current.addTo(mapRef.current);
        } else {
          externalLayersRef.current.removeFrom(mapRef.current);
        }
      }
    }
    
    return newVisibility;
  });
};

// Cambiar el mapa base
const changeBasemap = (basemapId: string) => {
  setActiveBasemap(basemapId);
};

// Procesar comando de IA
const handleAiCommandSubmit = async () => {
  if (!aiCommand.trim()) {
    setAiResponse('Por favor, ingresa un comando.');
    return;
  }
  
  setLoading(true);
  try {
    const command = aiCommand.toLowerCase();
    
    // Procesar el comando basado en palabras clave
    if (command.includes('pluma') || command.includes('dispersión')) {
      // Extraer parámetros si están presentes en el comando
      let direction = Math.floor(Math.random() * 360);
      let speed = 5 + Math.random() * 10;
      let concentration = 30 + Math.random() * 70;
      
      // Buscar dirección
      const dirMatch = command.match(/direcci[oó]n\s+(\d+)/i);
      if (dirMatch && dirMatch[1]) {
        direction = parseInt(dirMatch[1]);
      }
      
      // Buscar velocidad
      const speedMatch = command.match(/velocidad\s+(\d+(\.\d+)?)/i);
      if (speedMatch && speedMatch[1]) {
        speed = parseFloat(speedMatch[1]);
      }
      
      // Buscar concentración
      const concMatch = command.match(/concentraci[oó]n\s+(\d+(\.\d+)?)/i);
      if (concMatch && concMatch[1]) {
        concentration = parseFloat(concMatch[1]);
      }
      
      const success = calculateDispersionPlume({
        source: { 
          lat: initialCenter[0] + (Math.random() * 0.05), 
          lng: initialCenter[1] + (Math.random() * 0.05) 
        },
        direction,
        speed,
        concentration
      });
      
      if (success) {
        setAiResponse(`He creado una pluma de dispersión con dirección ${direction}°, velocidad ${speed.toFixed(1)} m/s y concentración ${concentration.toFixed(1)}%.`);
      } else {
        setAiResponse('No se pudo crear la pluma de dispersión. Verifica que el mapa esté cargado correctamente.');
      }
    } else if (command.includes('punto') || command.includes('marcador') || command.includes('poi')) {
      // Extraer nombre si está presente
      let name = 'Punto de IA';
      const nameMatch = command.match(/nombre\s+"([^"]+)"/i);
      if (nameMatch && nameMatch[1]) {
        name = nameMatch[1];
      }
      
      // Extraer tipo si está presente
      let type: POI['type'] = 'custom';
      if (command.includes('emergencia')) type = 'emergency';
      else if (command.includes('escuela')) type = 'school';
      else if (command.includes('comercial')) type = 'commercial';
      else if (command.includes('landmark')) type = 'landmark';
      
      // Crear nuevo POI
      const newPoi = {
        name,
        lat: initialCenter[0] + (Math.random() * 0.05),
        lng: initialCenter[1] + (Math.random() * 0.05),
        type,
        status: 'active' as const,
        metadata: { source: 'ai_command', createdAt: new Date().toISOString() },
        chatMessages: []
      };
      
      addPOI(newPoi);
      
      // Inicializar chat con mensaje de bienvenida
      setTimeout(() => {
        const addedPoi = pois.find(p => 
          p.name === name && 
          p.metadata.source === 'ai_command'
        );
        
        if (addedPoi) {
          // Agregar mensaje de sistema inicial
          const initialMessage: ChatMessage = {
            id: `msg_init_${Date.now()}`,
            poiId: addedPoi.id,
            content: `Punto "${name}" creado. ¿Qué información deseas registrar?`,
            timestamp: new Date(),
            sender: 'system',
            contentType: 'text'
          };
          
          // Actualizar el POI con el mensaje inicial
          updatePOI(addedPoi.id, {
            chatMessages: [initialMessage]
          });
          
          // Abrir el chat
          const updatedPoi = pois.find(p => p.id === addedPoi.id);
          if (updatedPoi) {
            setActiveChatPOI(updatedPoi);
            setShowChat(true);
          }
        }
      }, 500);
      
      setAiResponse(`He creado un nuevo punto "${name}" de tipo ${type}. Abriendo chat para configuración.`);
    } else if (command.includes('chat') || command.includes('conversación')) {
      // Buscar información sobre un punto específico o abrir un chat
      const poiMatch = command.match(/para\s+"([^"]+)"/i) || command.match(/con\s+"([^"]+)"/i);
      let targetPoi: POI | undefined;
      
      if (poiMatch && poiMatch[1]) {
        const poiName = poiMatch[1];
        targetPoi = pois.find(p => p.name.toLowerCase().includes(poiName.toLowerCase()));
      } else {
        // Si no se especifica un POI, usar el más cercano al centro
        targetPoi = pois.sort((a, b) => {
          const distA = Math.sqrt(Math.pow(a.lat - initialCenter[0], 2) + Math.pow(a.lng - initialCenter[1], 2));
          const distB = Math.sqrt(Math.pow(b.lat - initialCenter[0], 2) + Math.pow(b.lng - initialCenter[1], 2));
          return distA - distB;
        })[0];
      }
      
      if (targetPoi) {
        setActiveChatPOI(targetPoi);
        setShowChat(true);
        setAiResponse(`Abriendo chat para "${targetPoi.name}".`);
        
        // Si el mapa está disponible, centrar en el punto
        if (mapRef.current) {
          mapRef.current.setView([targetPoi.lat, targetPoi.lng], 15);
        }
      } else {
        setAiResponse('No se encontró ningún punto con ese nombre. Crea un punto primero.');
      }
    } else if (command.includes('zona') || command.includes('área') || command.includes('area')) {
      // Crear una zona simple alrededor del centro
      const centerLat = initialCenter[0] + (Math.random() * 0.05 - 0.025);
      const centerLng = initialCenter[1] + (Math.random() * 0.05 - 0.025);
      const radius = 0.01 + Math.random() * 0.02; // Radio aleatorio
      
      // Crear un polígono circular aproximado
      const points = 12;
      const coordinates: [number, number][] = [];
      
      for (let i = 0; i < points; i++) {
        const angle = (i / points) * Math.PI * 2;
        const x = centerLng + Math.cos(angle) * radius;
        const y = centerLat + Math.sin(angle) * radius;
        coordinates.push([x, y]);
      }
      
      // Cerrar el polígono
      coordinates.push([coordinates[0][0], coordinates[0][1]]);
      
      // Extraer nombre si está presente
      let name = 'Zona de IA';
      const nameMatch = command.match(/nombre\s+"([^"]+)"/i);
      if (nameMatch && nameMatch[1]) {
        name = nameMatch[1];
      }
      
      // Extraer tipo si está presente
      let type: Zone['type'] = 'custom';
      if (command.includes('emergencia')) type = 'emergency';
      else if (command.includes('ruta')) type = 'route';
      else if (command.includes('dispersión') || command.includes('dispersion')) type = 'dispersion';
      else type = 'area';
      
      addZone({
        name,
        type,
        coordinates: [coordinates],
        metadata: { source: 'ai_command', createdAt: new Date().toISOString() }
      });
      
      setAiResponse(`He creado una nueva zona "${name}" de tipo ${type}.`);
    } else if (command.includes('chatbot') || command.includes('asistente')) {
      // Comandos relacionados con el chatbot
      if (command.includes('todos') || command.includes('global')) {
        // Mensaje global a todos los puntos
        const messageMatch = command.match(/mensaje\s+"([^"]+)"/i);
        let message = 'Mensaje del sistema';
        
        if (messageMatch && messageMatch[1]) {
          message = messageMatch[1];
        }
        
        // Añadir el mensaje a todos los puntos
        pois.forEach(poi => {
          const systemMessage: ChatMessage = {
            id: `msg_global_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            poiId: poi.id,
            content: message,
            timestamp: new Date(),
            sender: 'system',
            contentType: 'text'
          };
          
          updatePOI(poi.id, {
            chatMessages: [...(poi.chatMessages || []), systemMessage]
          });
        });
        
        setAiResponse(`Mensaje global enviado a todos los puntos: "${message}"`);
      } else {
        setAiResponse('El chatbot está listo para ayudarte. Puedes abrir un chat específico con un punto o crear un punto nuevo.');
      }
    } else if (command.includes('ayuda') || command.includes('help') || command.includes('comandos')) {
      // Mostrar ayuda
      setAiResponse(`
        Comandos disponibles:
        - "crear pluma de dispersión [dirección X] [velocidad Y] [concentración Z]"
        - "agregar punto [nombre "X"] [tipo: emergencia/escuela/comercial]"
        - "crear zona [nombre "X"] [tipo: emergencia/ruta/área]"
        - "abrir chat para "Nombre del punto"" - Abre el chat de un punto existente
        - "chatbot mensaje global "Texto del mensaje"" - Envía mensaje a todos los puntos
        - "ayuda" o "comandos" para ver esta lista
      `);
    } else {
      setAiResponse(`Comando no reconocido. Prueba con: "crear pluma de dispersión", "agregar punto", "crear zona", "abrir chat" o "ayuda".`);
    }
  } catch (error) {
    console.error('Error procesando comando IA:', error);
    setAiResponse(`Error procesando tu comando: ${error instanceof Error ? error.message : String(error)}`);
  } finally {
    setLoading(false);
  }
};

// Limpiar recursos al desmontar
useEffect(() => {
  return () => {
    if (mapRef.current) {
      mapRef.current.off('moveend', handleMapChange);
      
      if (L.Draw && L.Draw.Event) {
        mapRef.current.off(L.Draw.Event.CREATED, handleDrawCreated);
      }
      
      if (drawControlRef.current) {
        mapRef.current.removeControl(drawControlRef.current);
      }
    }
  };
}, []);

// Gestión de mensajes para exportar GeoJSON con información de chat
const exportToGeoJSON = () => {
  const geojson = {
    type: 'FeatureCollection',
    features: [
      ...pois.map(point => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.lng, point.lat],
        },
        properties: {
          id: point.id,
          type: point.type,
          name: point.name,
          status: point.status,
          chatMessages: point.chatMessages?.map(msg => ({
            content: msg.content,
            timestamp: msg.timestamp,
            sender: msg.sender,
            contentType: msg.contentType,
          })),
          ...point.metadata,
        },
      })),
      ...zones.map(zone => ({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: zone.coordinates,
        },
        properties: {
          id: zone.id,
          type: zone.type,
          name: zone.name,
          ...zone.metadata,
        },
      })),
      ...dispersionPlumes.map(plume => ({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: plume.coordinates,
        },
        properties: {
          id: plume.id,
          type: 'dispersion',
          direction: plume.direction,
          speed: plume.speed,
          concentration: plume.concentration,
        },
      })),
    ],
  };
  
  // Crear enlace de descarga
  const blob = new Blob([JSON.stringify(geojson)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'xgis_export.geojson';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  // Mostrar mensaje de confirmación
  setAiResponse('GeoJSON exportado correctamente con toda la información de chat.');
};

// Añadir un nuevo POI
const addPOI = (poiData: Omit<POI, 'id'>) => {
  const newPOI: POI = {
    ...poiData,
    id: `poi_${Date.now()}`,
    chatMessages: poiData.chatMessages || [] // Inicializar array de mensajes de chat
  };
  
  setPois(prevPOIs => [...prevPOIs, newPOI]);
  
  // Actualizar mapa
  if (pointLayersRef.current && mapRef.current) {
    const marker = L.marker([newPOI.lat, newPOI.lng], {
      icon: getPOIIcon(newPOI.type),
    }).bindPopup(`
      <div>
        <h4>${newPOI.name}</h4>
        <p>${newPOI.metadata.address || ''}</p>
        <button class="btn btn-primary btn-sm mt-2 open-chat-btn" data-poi-id="${newPOI.id}">Abrir Chat</button>
      </div>
    `);
    
    // Asignar ID de POI al marcador
    (marker as any).poiId = newPOI.id;
    
    // Añadir evento para abrir chat
    marker.on('popupopen', () => {
      setTimeout(() => {
        const chatButton = document.querySelector(`.open-chat-btn[data-poi-id="${newPOI.id}"]`);
        if (chatButton) {
          chatButton.addEventListener('click', (e) => {
            e.preventDefault();
            setActiveChatPOI(newPOI);
            setShowChat(true);
            mapRef.current?.closePopup();
          });
        }
      }, 100);
    });
    
    marker.addTo(pointLayersRef.current);
  }
};

// Añadir una nueva zona
const addZone = (zoneData: Omit<Zone, 'id'>) => {
  const newZone: Zone = {
    ...zoneData,
    id: `zone_${Date.now()}`,
  };
  
  setZones(prevZones => [...prevZones, newZone]);
  
  // Actualizar mapa
  if (zoneLayersRef.current && mapRef.current) {
    const polygon = L.polygon(
      newZone.coordinates[0].map(coord => [coord[1], coord[0]]) as L.LatLngExpression[],
      getZoneStyle(newZone.type)
    ).bindPopup(`<div><h4>${newZone.name}</h4></div>`);
    
    polygon.addTo(zoneLayersRef.current);
  }
};

// Componente para el panel del mapa
const MapPanel = () => {
  // Si hay un tool o POI activo, mostrar detalles
  if (activeTool) {
    return (
      <Card className="h-100">
        <Card.Header className="p-3 bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{gisTools.find(t => t.id === activeTool)?.name || "Herramienta"}</h5>
          <Badge bg={
            gisTools.find(t => t.id === activeTool)?.status === 'running' ? 'success' :
            gisTools.find(t => t.id === activeTool)?.status === 'error' ? 'danger' : 'secondary'
          }>
            {gisTools.find(t => t.id === activeTool)?.status === 'running' ? 'En ejecución' :
             gisTools.find(t => t.id === activeTool)?.status === 'error' ? 'Error' : 'Detenido'}
          </Badge>
        </Card.Header>
        <Card.Body>
          <div className="mb-3">
            <strong>Descripción:</strong> {gisTools.find(t => t.id === activeTool)?.description}
          </div>
          <div className="mb-3">
            <strong>Comando:</strong> 
            <code className="d-block bg-light p-2 rounded">{gisTools.find(t => t.id === activeTool)?.command}</code>
          </div>
          <div className="d-flex gap-2 mb-4">
            <Button 
              variant="primary" 
              onClick={() => {
                // Acción ejecutar
                const tool = gisTools.find(t => t.id === activeTool);
                if (tool) {
                  setGisTools(prev => 
                    prev.map(t => 
                      t.id === activeTool ? { ...t, status: 'running', lastRun: new Date() } : t
                    )
                  );
                }
              }}
              className="d-flex align-items-center"
            >
              <IconWrapper icon={BarChart3} className="me-2" />
              Ejecutar Análisis
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={() => setActiveTool(undefined)}
              className="d-flex align-items-center"
            >
              <IconWrapper icon={MapIcon} className="me-2" />
              Volver al Mapa
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }
  
  if (activePOI) {
    const selectedPOI = pois.find(p => p.id === activePOI);
    return (
      <Card className="h-100">
        <Card.Header className="p-3 bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{selectedPOI?.name || "Punto de Interés"}</h5>
          <Badge bg={
            selectedPOI?.status === 'active' ? 'success' :
            selectedPOI?.status === 'warning' ? 'warning' : 'danger'
          }>
            {selectedPOI?.status === 'active' ? 'Activo' :
             selectedPOI?.status === 'warning' ? 'Advertencia' : 'Inactivo'}
          </Badge>
        </Card.Header>
        <Card.Body>
          {selectedPOI && (
            <>
              <div className="mb-3">
                <strong>Coordenadas:</strong> [{selectedPOI.lat.toFixed(4)}, {selectedPOI.lng.toFixed(4)}]
              </div>
              <div className="mb-3">
                <strong>Tipo:</strong> {selectedPOI.type}
              </div>
              {Object.entries(selectedPOI.metadata).map(([key, value]) => (
                <div className="mb-2" key={key}>
                  <strong>{key}:</strong> {value}
                </div>
              ))}
              <div className="d-flex gap-2 mt-4">
                <Button 
                  variant="primary" 
                  onClick={() => {
                    if (mapRef.current) {
                      mapRef.current.setView([selectedPOI.lat, selectedPOI.lng], 15);
                      setActivePOI(undefined);
                    }
                  }}
                  className="d-flex align-items-center"
                >
                  <IconWrapper icon={MapPin} className="me-2" />
                  Localizar en Mapa
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setActivePOI(undefined)}
                  className="d-flex align-items-center"
                >
                  <IconWrapper icon={MapIcon} className="me-2" />
                  Volver al Mapa
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    );
  }

  // Mapa por defecto
  return (
    <Card className="h-100">
      <Card.Header className="p-3 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Visualizador GIS</h5>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={exportToGeoJSON}
          >
            <IconWrapper icon={DownloadCloud} className="me-1" /> Exportar
          </Button>
          <Form.Control 
            type="text" 
            placeholder="Ejecutar comando IA..." 
            size="sm" 
            className="w-50"
            value={aiCommand}
            onChange={(e) => setAiCommand(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAiCommandSubmit()}
          />
          <Button 
            variant="primary" 
            size="sm"
            onClick={handleAiCommandSubmit}
            disabled={loading}
          >
            {loading ? 'Procesando...' : 'Ejecutar'}
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0 position-relative">
        <div style={{ height: '600px', width: '100%' }}>
          <MapContainer
            center={initialCenter}
            zoom={initialZoom}
            style={{ height: '100%', width: '100%' }}
          >
            <MapInitializer onMapReady={handleMapReady} />
            
            <TileLayer
              url={
                activeBasemap === 'satellite' 
                  ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  : activeBasemap === 'terrain'
                    ? "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                    : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              }
              attribution={
                activeBasemap === 'satellite'
                  ? '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                  : activeBasemap === 'terrain'
                    ? 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
                    : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              }
            />
          </MapContainer>
        </div>
        
        {/* Chat flotante para POI */}
        {showChat && activeChatPOI && (
          <div 
            className="position-absolute" 
            style={{ 
              top: '10px', 
              right: '10px', 
              width: '350px',
              height: '580px',
              zIndex: 1000,
              backgroundColor: 'white',
              boxShadow: '0 0 10px rgba(0,0,0,0.2)',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <POIChat 
              poi={activeChatPOI} 
              onMessageSend={handlePoiMessage}
              onClose={() => {
                setShowChat(false);
                setActiveChatPOI(null);
              }}
            />
          </div>
        )}
        
        {aiResponse && (
          <div className="mt-2 p-2 bg-light border rounded">
            <div className="d-flex align-items-center">
              <IconWrapper icon={Layers} className="me-2 text-primary" />
              <strong>Respuesta:</strong> <span className="ms-2">{aiResponse}</span>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// Estadísticas del dashboard
const stats = getStats();
const gisStats = [
  {
    title: 'Alertas Activas',
    value: alerts.filter(a => !a.resolved).length,
    icon: <IconWrapper icon={AlertTriangle} size={24} />,
    variant: 'danger',
    trend: 12
  },
  {
    title: 'Puntos Activos',
    value: `${stats.activePOIs}/${pois.length}`,
    icon: <IconWrapper icon={MapPin} size={24} />,
    variant: 'success',
    trend: 2
  },
  {
    title: 'Zonas Registradas',
    value: stats.activeZones,
    icon: <IconWrapper icon={Layers} size={24} />,
    variant: 'primary',
    trend: 1
  },
  {
    title: 'Herramientas Activas',
    value: `${stats.toolsRunning}/${gisTools.length}`,
    icon: <IconWrapper icon={Settings} size={24} />,
    variant: 'warning',
    trend: 0
  }
];

// Componente para mostrar estadísticas
const GISStat = ({ title, value, icon, variant, trend }: GISStatProps) => (
  <Card className="h-100">
    <Card.Body className="p-3">
      <div className="d-flex align-items-center">
        <div 
          className={`me-3 d-flex align-items-center justify-content-center rounded-circle bg-${variant} bg-opacity-10`} 
          style={{ width: 48, height: 48 }}
        >
          {icon}
        </div>
        <div>
          <h6 className="text-body-tertiary fw-semibold mb-0">{title}</h6>
          <h5 className="fw-bold mb-0">{value}</h5>
          {typeof trend === 'number' && (
            <span className={`fs-sm ${trend >= 0 ? 'text-success' : 'text-danger'}`}>
              {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs ayer
            </span>
          )}
        </div>
      </div>
    </Card.Body>
  </Card>
);

// Renderizado del dashboard completo
return (
  <XGISDashboardContext.Provider value={contextValue}>
    <Row className="gy-3 mb-6 justify-content-between">
      <Col md={9} xs="auto">
        <h2 className="mb-2 text-body-emphasis">Dashboard GIS Integrado</h2>
        <h5 className="text-body-tertiary fw-semibold">
          Monitoreo y gestión de información geográfica
        </h5>
      </Col>
      <Col md={3} xs="auto">
        <DatePicker
          options={{
            defaultDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          }}
        />
      </Col>
    </Row>
    
    {/* Estadísticas */}
    <Row className="mb-3 gy-3">
      {gisStats.map((stat, index) => (
        <Col xs={12} sm={6} md={3} key={index}>
          <GISStat {...stat} />
        </Col>
      ))}
    </Row>
    
    {/* Panel principal reorganizado */}
    <Row className="mb-3 gy-3">
      <Col xs={12} md={4} xl={3}>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k as 'tools' | 'pois' | 'layers')}
          className="mb-3"
        >
          <Tab eventKey="tools" title="Herramientas">
            <GISTools 
              tools={gisTools} 
              onSelect={setActiveTool} 
              activeToolId={activeTool}
            />
          </Tab>
          <Tab eventKey="pois" title="Puntos">
            <POIsList 
              points={pois} 
              onSelect={setActivePOI} 
              activePoiId={activePOI}
            />
          </Tab>
          <Tab eventKey="layers" title="Capas">
            <Card className="h-100 bg-dark">
              <Card.Header className="p-3 border-bottom border-secondary">
                <h5 className="mb-0 text-white">Gestión de Capas</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <h6 className="text-white mb-2">Mapa Base</h6>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant={activeBasemap === 'streets' ? 'primary' : 'outline-secondary'}
                      onClick={() => changeBasemap('streets')}
                    >
                      Calles
                    </Button>
                    <Button
                      size="sm"
                      variant={activeBasemap === 'satellite' ? 'primary' : 'outline-secondary'}
                      onClick={() => changeBasemap('satellite')}
                    >
                      Satélite
                    </Button>
                    <Button
                      size="sm"
                      variant={activeBasemap === 'terrain' ? 'primary' : 'outline-secondary'}
                      onClick={() => changeBasemap('terrain')}
                    >
                      Terreno
                    </Button>
                  </div>
                </div>
                
                <div>
                  <h6 className="text-white mb-2">Capas Visibles</h6>
                  <Form.Check 
                    type="switch"
                    id="points-layer"
                    label="Puntos de Interés"
                    checked={visibleLayers.points}
                    onChange={() => toggleLayerVisibility('points')}
                    className="mb-2 text-white"
                  />
                  <Form.Check 
                    type="switch"
                    id="zones-layer"
                    label="Zonas y Áreas"
                    checked={visibleLayers.zones}
                    onChange={() => toggleLayerVisibility('zones')}
                    className="mb-2 text-white"
                  />
                  <Form.Check 
                    type="switch"
                    id="plumes-layer"
                    label="Plumas de Dispersión"
                    checked={visibleLayers.plumes}
                    onChange={() => toggleLayerVisibility('plumes')}
                    className="mb-2 text-white"
                  />
                  <Form.Check 
                    type="switch"
                    id="external-layer"
                    label="Datos Externos"
                    checked={visibleLayers.external}
                    onChange={() => toggleLayerVisibility('external')}
                    className="mb-2 text-white"
                  />
                </div>
                
                <div className="mt-4">
                  <h6 className="text-white mb-2">Datos Externos</h6>
                  <div className="input-group mb-2">
                    <Form.Control
                      placeholder="URL de GeoJSON..."
                      aria-label="URL de GeoJSON"
                      className="bg-dark text-white border-secondary"
                      onChange={(e) => setAiCommand(e.target.value)}
                    />
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => fetchExternalData(aiCommand)}
                      disabled={loading}
                    >
                      {loading ? 'Cargando...' : 'Cargar'}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Col>
      <Col xs={12} md={8} xl={9}>
        <MapPanel />
      </Col>
    </Row>
    
    {/* Alertas Geográficas */}
    <Row className="mb-3">
      <Col xs={12}>
        <Card className="h-100 bg-dark">
          <Card.Header className="p-3 d-flex justify-content-between align-items-center border-bottom border-secondary">
            <h5 className="mb-0 text-white">Alertas Geográficas</h5>
            <Button variant="outline-light" size="sm">
              <IconWrapper icon={RefreshCw} className="me-1" /> Actualizar
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            {alerts.filter(a => !a.resolved).length === 0 ? (
              <div className="text-center p-4 text-muted">
                <IconWrapper icon={Shield} size={32} className="mb-2 text-secondary" />
                <p>No hay alertas activas en este momento</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover variant="dark" className="mb-0">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Severidad</th>
                      <th>Mensaje</th>
                      <th>Ubicación</th>
                      <th>Tiempo</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.filter(a => !a.resolved).slice(0, 5).map(alert => {
                      const getAlertVariant = (severity: GeoAlert['severity']): string => {
                        switch (severity) {
                          case 'critical': return 'danger';
                          case 'high': return 'warning';
                          case 'medium': return 'primary';
                          case 'low': return 'info';
                          default: return 'secondary';
                        }
                      };

                      const getTimeAgo = (date: Date): string => {
                        const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
                        if (seconds < 60) return `${seconds}s`;
                        if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
                        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
                        return `${Math.floor(seconds / 86400)}d`;
                      };

                      return (
                        <tr key={alert.id}>
                          <td>{alert.type}</td>
                          <td>
                            <Badge bg={getAlertVariant(alert.severity)}>
                              {alert.severity.toUpperCase()}
                            </Badge>
                          </td>
                          <td>{alert.message}</td>
                          <td>[{alert.location.lat.toFixed(4)}, {alert.location.lng.toFixed(4)}]</td>
                          <td>{getTimeAgo(alert.timestamp)}</td>
                          <td>
                            <Button 
                              variant="outline-light" 
                              size="sm" 
                              className="me-2 p-1"
                              onClick={() => {
                                if (mapRef.current) {
                                  mapRef.current.setView([alert.location.lat, alert.location.lng], 15);
                                }
                              }}
                            >
                              <IconWrapper icon={MapPin} size={16} />
                            </Button>
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              className="p-1"
                              onClick={() => {
                                setAlerts(prev => 
                                  prev.map(a => a.id === alert.id ? { ...a, resolved: true } : a)
                                );
                              }}
                            >
                              <IconWrapper icon={Shield} size={16} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
    
    {/* Gráficas y estadísticas */}
    <Row className="mb-3">
      <Col xs={12} lg={8}>
        <ActivityCharts />
      </Col>
      <Col xs={12} lg={4}>
        <ChatStats pois={pois} />
      </Col>
    </Row>
    
    {/* Estado del sistema */}
    <div className="bg-dark p-3 rounded mb-3 border border-secondary">
      <Row className="align-items-center">
        <Col>
          <h6 className="mb-0 d-flex align-items-center text-white">
            Estado del Sistema 
            <Badge bg="success" className="ms-2">OPERATIVO</Badge>
          </h6>
        </Col>
        <Col xs="auto">
          <div className="d-flex gap-3">
            <span className="d-flex align-items-center">
              <span className="badge rounded-circle bg-success me-1" style={{ width: 8, height: 8 }}></span>
              <span className="text-white">GPS: Conectado</span>
            </span>
            <span className="d-flex align-items-center">
              <span className="badge rounded-circle bg-success me-1" style={{ width: 8, height: 8 }}></span>
              <span className="text-white">API: Activa</span>
            </span>
            <span className="d-flex align-items-center">
              <span className="badge rounded-circle bg-success me-1" style={{ width: 8, height: 8 }}></span>
              <span className="text-white">Servidor: En línea</span>
            </span>
            <span className="d-flex align-items-center">
              <span className={`badge rounded-circle ${stats.criticalAlerts > 0 ? 'bg-danger' : 'bg-success'} me-1`} style={{ width: 8, height: 8 }}></span>
              <span className="text-white">Alertas críticas: {stats.criticalAlerts}</span>
            </span>
          </div>
        </Col>
      </Row>
    </div>
  </XGISDashboardContext.Provider>
);
};

export default XGISDashboard;